import { Flex, Table, Text, WarningIcon } from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import {
  useAccountTitleOptions,
  useAccountingTypeOptions,
} from '@/features/master-table/hooks';
import { useProject } from '@/features/project/_hooks/project';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import {
  BigCalculator,
  formatValueByUnit,
  handleZeroValueWithNegativeCount,
  isPS,
} from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = Pick<
  z.infer<typeof ProjectModel>,
  'fiscalYear' | 'fundAdditionalAttributes'
> & { priorProjectId: string };

export const FundInvestmentsInformation: FC<Props> = ({
  priorProjectId,
  fiscalYear,
  fundAdditionalAttributes,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { id: projectId } = useId();
  const priorProject = useProject(priorProjectId);
  const accountingTypeOptions = useAccountingTypeOptions();
  const accountTitleOptions = useAccountTitleOptions();
  const { devtoolState } = useDevToolsStorage();
  const isDebugMode = devtoolState?.isDebugMode;

  const investments = fundAdditionalAttributes?.investments;

  const accumulatedAmount = useMemo<number | string | null>(() => {
    const amounts = investments?.accumulatedInvestmentsFromTheGovernment.map(
      (v) => v.amount,
    );
    if (!amounts?.length) {
      return null;
    }

    const value = amounts.reduce(
      (sum, current) => BigCalculator.add(sum, current),
      0,
    );

    const { fallbackValue } = handleZeroValueWithNegativeCount(value, {
      unit,
      isDebugMode,
      negativeCount: amounts.filter((v) => v < 0).length,
    });
    return typeof fallbackValue === 'string' ? fallbackValue : value;
  }, [investments, unit, isDebugMode]);

  const amountTwoYearsAgo =
    investments?.annualInvestmentsFromTheGovernment?.at(0);
  const amountLastYear = investments?.annualInvestmentsFromTheGovernment?.at(1);
  const amountCurrentYear =
    investments?.annualInvestmentsFromTheGovernment?.at(2);
  const privateInvestmentAmount =
    investments?.accumulatedInvestmentFromPrivateSectors;

  const renderAmountText = (value: Parameters<typeof formatValueByUnit>[0]) => {
    return formatValueByUnit(value, {
      operation: 'round',
      unit,
      showUnitLabel: true,
    });
  };

  return (
    <Flex vertical gap={24}>
      <Text variant="h5" text={t('accumulated-investment-amount')} />
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType: 'KS_INVESTMENT',
          ksInvestment: {
            path: { project: projectId },
            field: 'ks.investment.accumulated-amount-table',
          },
        })}
      >
        <Table
          tableLayout="fixed"
          pagination={false}
          columns={[
            {
              title: t('investment-amount-from-the-government'),
              children: [
                {
                  title: t('accumulated-amount'),
                  dataIndex: 'accumulatedAmount',
                  render: (_, record) => {
                    return (
                      <Text
                        text={
                          typeof record.accumulatedAmount === 'string'
                            ? record.accumulatedAmount
                            : renderAmountText(record.accumulatedAmount)
                        }
                      />
                    );
                  },
                },
                {
                  title: t('investment-amount-by-year'),
                  children: [
                    {
                      title: t('fiscal-year-with-value', {
                        year: fiscalYear - 2,
                      }),
                      dataIndex: 'amountTwoYearsAgo',
                      render:
                        isBefore2025({ fiscalYear }) || isPS()
                          ? (value) => <Text text={renderAmountText(value)} />
                          : (value) => {
                              if (
                                !priorProject.data?.fundAdditionalAttributes
                                  ?.investments
                                  ?.annualInvestmentsFromTheGovernment
                              ) {
                                return <Text text={renderAmountText(value)} />;
                              }
                              return (
                                <Flex gap={4}>
                                  {value !==
                                    priorProject.data.fundAdditionalAttributes
                                      ?.investments
                                      ?.annualInvestmentsFromTheGovernment?.[1] && (
                                    <WarningIcon
                                      hint={t(
                                        'warning-message-different-from-reference-value',
                                      )}
                                    />
                                  )}
                                  <Text text={renderAmountText(value)} />
                                </Flex>
                              );
                            },
                    },
                    {
                      title: t('fiscal-year-with-value', {
                        year: fiscalYear - 1,
                      }),
                      dataIndex: 'amountLastYear',
                      render: (value) => (
                        <Text text={renderAmountText(value)} />
                      ),
                    },
                    {
                      title: t('fiscal-year-with-value', { year: fiscalYear }),
                      dataIndex: 'amountCurrentYear',
                      render: (value) => (
                        <Text text={renderAmountText(value)} />
                      ),
                    },
                  ],
                },
              ],
            },
            {
              title: t(
                'accumulated-investment-amount-from-the-private-sectors',
              ),
              dataIndex: 'privateInvestmentAmount',
              render: (value) => <Text text={renderAmountText(value)} />,
            },
          ]}
          dataSource={[
            {
              key: '1',
              accumulatedAmount,
              amountTwoYearsAgo,
              amountLastYear,
              amountCurrentYear,
              privateInvestmentAmount,
            },
          ]}
          bordered
        />
      </PinpointMarker>
      <Text
        variant="h5"
        text={t(
          'accumulated-investment-amount-from-the-government-in-accounting-breakdown',
        )}
      />
      <PinpointMarker
        pinpoint={getPinpointSceneAndFieldBySheetType({
          pinpointSheetType: 'KS_INVESTMENT',
          ksInvestment: {
            path: { project: projectId },
            field: 'ks.investment.accumulated-amount-from-the-government-table',
          },
        })}
      >
        <Table
          tableLayout="fixed"
          pagination={false}
          columns={[
            {
              title: t('accounting-name'),
              dataIndex: 'accountingTypeName',
              render: (value) => <Text text={value} />,
            },
            {
              title: t('account-title'),
              dataIndex: 'accountingTitle',
              render: (value) => <Text text={value} />,
            },
            {
              title: t('investment-amount'),
              dataIndex: 'amount',
              render: (value) => <Text text={value} />,
            },
          ]}
          dataSource={investments?.accumulatedInvestmentsFromTheGovernment.map(
            (item, index) => ({
              key: index,
              accountingTypeName:
                accountingTypeOptions.data?.find(
                  (option) => option.value === item.accountingTypeId,
                )?.label ?? item.optionalAccountingTypeName,
              accountingTitle:
                accountTitleOptions.data?.find(
                  (option) => option.value === item.accountTitleId,
                )?.label ?? item.optionalAccountTitleName,
              amount: renderAmountText(item.amount),
            }),
          )}
        />
      </PinpointMarker>
    </Flex>
  );
};
